import React, { useState, useEffect } from "react";
import { Mail, FileText } from "lucide-react";
import {
	CraftModal,
	CraftModalHeader,
	CraftModalBody,
	CraftModalFooter,
	CraftButton,
	CraftInputText,
} from "@craft-design-system/core-components-react";
import { apiClient } from "../../services/api";

/**
 * FloatingExportModal
 *
 * Fire-and-forget export modal for the Floating Series Dashboard.
 * Calls POST /export-floating-email, receives 202 immediately, and lets the
 * user close the window while the export runs in the background.
 * The user receives the download link by email when the export is ready.
 *
 * The floating source table (floating_dashboard_data) only supports project
 * (projektnummer) and change-number (aendnr) filters.
 */
const FloatingExportModal = ({
	isOpen,
	onClose,
	userEmail = "",
	currentFilters = {},
}) => {
	const [selectedFormat, setSelectedFormat] = useState("");
	const [recipientEmail, setRecipientEmail] = useState(userEmail);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [jobQueued, setJobQueued] = useState(false);

	// Reset when modal opens
	useEffect(() => {
		if (isOpen) {
			setRecipientEmail(userEmail);
			setJobQueued(false);
			setSelectedFormat("");
		}
	}, [isOpen, userEmail]);

	if (!isOpen) return null;

	// Count active filters for display
	const activeFilterCount = Object.values(currentFilters).reduce(
		(count, val) => {
			if (val === null || val === undefined) return count;
			if (Array.isArray(val)) return count + (val.length > 0 ? 1 : 0);
			return count + (String(val).trim() !== "" ? 1 : 0);
		},
		0,
	);

	const handleExport = async () => {
		if (!recipientEmail.trim()) {
			alert("Please enter a recipient email address.");
			return;
		}

		setIsSubmitting(true);
		try {
			const payload = {
				requester_email: recipientEmail.trim(),
				export_format: selectedFormat,
				project_number: currentFilters.project_number || [],
				alg: currentFilters.alg || [],
				change_no: currentFilters.change_no || [],
			};
			await apiClient.post("/export-floating-email", payload);
			// Backend returns 202 — job is running in the background
			setJobQueued(true);
		} catch (error) {
			console.error("Floating export error:", error);

			const status = error?.response?.status;
			const isNetworkOrGatewayError =
				!status || status === 502 || status === 503 || status === 504;

			if (isNetworkOrGatewayError) {
				console.warn(
					"Floating export: received gateway/network error but the export job was " +
						"already submitted to the backend. Showing queued confirmation.",
					error,
				);
				setJobQueued(true);
			} else {
				alert(
					error?.response?.data?.detail ||
						error?.message ||
						"Export failed to start. Please try again.",
				);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const styles = {
		filterInfoBox: {
			backgroundColor: "#f0f8ff",
			border: "1px solid #b3d9ff",
			borderRadius: "6px",
			padding: "12px 14px",
			fontSize: "13px",
			color: "#1a5276",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			lineHeight: "1.5",
			marginBottom: "16px",
		},
		infoBox: {
			backgroundColor: "#f0f8ff",
			border: "1px solid #b3d9ff",
			borderRadius: "6px",
			padding: "12px 14px",
			fontSize: "13px",
			color: "#1a5276",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			lineHeight: "1.5",
			marginTop: "16px",
		},
		formGroup: {
			display: "flex",
			flexDirection: "column",
			gap: "6px",
			marginBottom: "16px",
		},
		label: {
			fontSize: "13px",
			fontWeight: "bold",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			color: "#333",
			marginBottom: "4px",
			display: "block",
		},
		select: {
			padding: "9px 12px",
			border: "1px solid #ddd",
			borderRadius: "4px",
			fontSize: "14px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			backgroundColor: "white",
			cursor: "pointer",
			width: "100%",
		},
		hint: {
			fontSize: "12px",
			color: "#888",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			marginTop: "4px",
		},
		spinner: {
			width: "14px",
			height: "14px",
			border: "2px solid rgba(255,255,255,0.4)",
			borderTop: "2px solid white",
			borderRadius: "50%",
			animation: "spin 0.8s linear infinite",
			display: "inline-block",
		},
		successIcon: { fontSize: "52px", textAlign: "center", marginBottom: "8px" },
		successTitle: {
			fontSize: "18px",
			fontWeight: "bold",
			color: "#28a745",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			textAlign: "center",
			marginBottom: "16px",
		},
		detailBox: {
			backgroundColor: "#f0f8ff",
			border: "1px solid #b3d9ff",
			borderRadius: "8px",
			padding: "16px 18px",
			fontSize: "14px",
			lineHeight: "1.7",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			marginBottom: "16px",
		},
		confirmBox: {
			backgroundColor: "#e8f5e9",
			border: "1px solid #a5d6a7",
			borderRadius: "8px",
			padding: "14px 16px",
			fontSize: "13px",
			color: "#2e7d32",
			textAlign: "center",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
	};

	return (
		<>
			<style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>

			<CraftModal show={isOpen} allowClickOut={false} onCraftClose={onClose}>
				<CraftModalHeader
					header="Export Floating Dashboard Data"
					content={
						!jobQueued
							? "Select your export format and email address. The export runs in the background — you will receive a download link by email."
							: "Your export has been queued successfully."
					}
				/>

				<CraftModalBody>
					{!jobQueued ? (
						<>
							{/* Format selector */}
							<div style={styles.formGroup}>
								<label style={styles.label}>
									<FileText
										size={13}
										style={{ verticalAlign: "middle", marginRight: "4px" }}
									/>
									Export Format
								</label>
								<select
									value={selectedFormat}
									onChange={(e) => setSelectedFormat(e.target.value)}
									style={styles.select}
									disabled={isSubmitting}
								>
									<option value="" disabled>
										— Select format —
									</option>
									<option value="csv">CSV (.csv)</option>
									<option value="xlsx">Excel (.xlsx)</option>
								</select>
								<div
									style={{
										backgroundColor: "#fff3cd",
										border: "1px solid #ffc107",
										borderRadius: "4px",
										padding: "8px 12px",
										fontSize: "12px",
										color: "#856404",
										marginTop: "6px",
										fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
									}}
								>
									⚠️ <strong>German locale users:</strong> Please select{" "}
									<strong>Excel (.xlsx)</strong> format. CSV exports may display
									corrupted characters in German Excel settings.
								</div>
							</div>

							{/* Filter summary */}
							<div style={styles.filterInfoBox}>
								{activeFilterCount > 0 ? (
									<>
										<strong>
											🔍 {activeFilterCount} active filter
											{activeFilterCount > 1 ? "s" : ""} applied
										</strong>
										<br />
										Only the currently filtered data will be exported.
									</>
								) : (
									<>
										<strong>📋 No filters active</strong>
										<br />
										The full Floating dashboard dataset will be exported.
									</>
								)}
							</div>

							{/* Email input */}
							<CraftInputText
								type="email"
								label="Send export to email"
								required
								value={recipientEmail}
								onInput={(e) => setRecipientEmail(e.target.value)}
								placeholder="your@email.com"
								disabled={isSubmitting}
								style={{ width: "100%", marginBottom: "4px" }}
							/>
							<span style={styles.hint}>
								A download link will be emailed to this address when the export
								is ready.
							</span>

							{/* Background export info */}
							<div style={styles.infoBox}>
								<strong>✨ Background Export:</strong> The export runs entirely
								in the background — you can close this window immediately after
								clicking Export.
								<br />
								Active filters are applied automatically.
							</div>
						</>
					) : (
						/* Confirmation screen */
						<>
							<div style={styles.successIcon}>📬</div>
							<div style={styles.successTitle}>Export Started!</div>
							<div style={styles.detailBox}>
								<div>
									<strong>Format:</strong> {selectedFormat.toUpperCase()}
								</div>
								<div>
									<strong>Filters:</strong>{" "}
									{activeFilterCount > 0
										? `${activeFilterCount} active filter${activeFilterCount > 1 ? "s" : ""}`
										: "None (full export)"}
								</div>
								<div>
									<strong>Recipient:</strong> {recipientEmail}
								</div>
							</div>
							<div style={styles.confirmBox}>
								✅ The export is processing in the background.
								<br />
								<strong>You can close this window now.</strong>
								<br />A download link will be emailed to{" "}
								<strong>{recipientEmail}</strong> when ready.
							</div>
						</>
					)}
				</CraftModalBody>

				<CraftModalFooter>
					{!jobQueued ? (
						<>
							<CraftButton
								variant="plain"
								size="m"
								onClick={onClose}
								disabled={isSubmitting}
							>
								Cancel
							</CraftButton>
							<CraftButton
								variant="primary"
								size="m"
								onClick={handleExport}
								disabled={
									isSubmitting || !recipientEmail.trim() || !selectedFormat
								}
							>
								{isSubmitting ? (
									<span
										style={{
											display: "flex",
											alignItems: "center",
											gap: "7px",
										}}
									>
										<span style={styles.spinner} />
										Starting…
									</span>
								) : (
									<span
										style={{
											display: "flex",
											alignItems: "center",
											gap: "7px",
										}}
									>
										<Mail size={15} />
										Export &amp; Email {selectedFormat.toUpperCase()}
									</span>
								)}
							</CraftButton>
						</>
					) : (
						<>
							<CraftButton
								variant="plain"
								size="m"
								onClick={() => setJobQueued(false)}
							>
								New Export
							</CraftButton>
							<CraftButton variant="primary" size="m" onClick={onClose}>
								Close
							</CraftButton>
						</>
					)}
				</CraftModalFooter>
			</CraftModal>
		</>
	);
};

export default FloatingExportModal;
